/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxfrag */
import React from 'react';

import { css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import type { IconButtonProps } from '@atlaskit/button/new';
import { css, jsx } from '@atlaskit/css';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import messages from './messages';

type Feedback = 'good' | 'bad';
type Props = {
	flexIconWrapper?: boolean;
	isDisabled?: boolean;
	isPressed?: Feedback;
	largeIconButton?: boolean;
	onBadResponseButtonClick: () => void;
	onGoodResponseButtonClick: () => void;
};

type FeedbackButtonProps = {
	flexIconWrapper?: boolean;
	isDisabled?: boolean;
	isPressed?: boolean;
	largeIconButton?: boolean;
	onClick: () => void;
	type: Feedback;
};

const iconWrapperStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const hideBackgroundStyles = cssUnbounded({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	button: {
		backgroundColor: token('color.background.neutral.subtle'),
	},
});

const Announcer = ({ message, shouldAnnounce }: { message: string; shouldAnnounce: boolean }) => {
	return (
		<VisuallyHidden>
			<Box aria-atomic="true" aria-live="polite">
				{shouldAnnounce && message}
			</Box>
		</VisuallyHidden>
	);
};

const FeedbackButton = ({
	type,
	onClick,
	isPressed,
	isDisabled,
	flexIconWrapper,
	largeIconButton,
}: FeedbackButtonProps) => {
	const { formatMessage } = useIntl();
	const responseLabel =
		type === 'good' ? formatMessage(messages.goodResponse) : formatMessage(messages.badResponse);
	const color = isPressed ? token('color.link.pressed') : 'currentColor';
	const Icon = fg('a11y-fix-feedback-icon-decorative')
		? () =>
				type === 'good' ? (
					<ThumbsUpIcon label="" color={color} />
				) : (
					<ThumbsDownIcon label="" color={color} />
				)
		: () =>
				type === 'good' ? (
					<ThumbsUpIcon label={responseLabel} color={color} />
				) : (
					<ThumbsDownIcon label={responseLabel} color={color} />
				);

	const tooltipOptions: IconButtonProps['tooltip'] = {
		position: 'top',
		content: responseLabel,
	};

	return (
		<div
			css={[
				flexIconWrapper && iconWrapperStyles,
				(isPressed || isDisabled) && hideBackgroundStyles,
			]}
		>
			<IconButton
				testId={`${type}-response-feedback-button`}
				type="button"
				isDisabled={isDisabled}
				onClick={onClick}
				icon={Icon}
				label={responseLabel}
				appearance="subtle"
				spacing={largeIconButton ? undefined : 'compact'}
				isTooltipDisabled={false}
				tooltip={tooltipOptions}
			/>
		</div>
	);
};

export const FeedbackForm = ({
	onGoodResponseButtonClick,
	onBadResponseButtonClick,
	isPressed,
	isDisabled,
	flexIconWrapper,
	largeIconButton,
}: Props) => {
	const { formatMessage } = useIntl();
	const feedbackWasSentLabel = formatMessage(messages.feedbackSent);
	const [feedbackSelected, setFeedbackSelected] = React.useState<Feedback>();
	const handleOnClick = React.useCallback(
		(onClick: () => void, feedback: Feedback) => () => {
			onClick();
			setFeedbackSelected(feedback);
		},
		[setFeedbackSelected],
	);
	const disableButton = !!isPressed || isDisabled || !!feedbackSelected;

	return (
		<React.Fragment>
			<Announcer message={feedbackWasSentLabel} shouldAnnounce={Boolean(feedbackSelected)} />
			<FeedbackButton
				type="good"
				isPressed={isPressed === 'good'}
				isDisabled={disableButton}
				flexIconWrapper={flexIconWrapper}
				largeIconButton={largeIconButton}
				onClick={handleOnClick(onGoodResponseButtonClick, 'good')}
			/>
			<FeedbackButton
				type="bad"
				isPressed={isPressed === 'bad'}
				isDisabled={disableButton}
				flexIconWrapper={flexIconWrapper}
				largeIconButton={largeIconButton}
				onClick={handleOnClick(onBadResponseButtonClick, 'bad')}
			/>
		</React.Fragment>
	);
};
